import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

type State = 'idle' | 'previewing' | 'confirming' | 'deleting' | 'done' | 'error';

const TestDataCleanup: React.FC = () => {
  const [state, setState] = useState<State>('idle');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [errors, setErrors] = useState<string[]>([]);
  const [phrase, setPhrase] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const callCleanup = async (dryRun: boolean) => {
    const result: any = await (client as any).mutations.cleanTestData({ dryRun });
    return JSON.parse(result.data);
  };

  const handlePreview = async () => {
    setState('previewing');
    try {
      const res = await callCleanup(true);
      setCounts(res.counts || {});
      setState('confirming');
    } catch (e: any) {
      setErrorMsg(e.message || 'Preview failed');
      setState('error');
    }
  };

  const handleDelete = async () => {
    setState('deleting');
    try {
      const res = await callCleanup(false);
      setCounts(res.counts || {});
      setErrors(res.errors || []);
      setState('done');
    } catch (e: any) {
      setErrorMsg(e.message || 'Deletion failed');
      setState('error');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">🧹 Test Data Cleanup</h2>
      <p className="text-sm text-gray-500 mb-4">Delete all E2E test data: members TFC-9000–9999, events prefixed [E2E TEST], and related records.</p>

      {state === 'idle' && (
        <button onClick={handlePreview} className="px-4 py-2 bg-fishing-blue text-white rounded-md hover:bg-blue-700 text-sm font-medium">
          Preview Test Data
        </button>
      )}

      {state === 'previewing' && <p className="text-gray-600 animate-pulse">Scanning for test data…</p>}

      {state === 'confirming' && (
        <div className="space-y-4">
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(counts).map(([k, v]) => (
                <tr key={k} className="border-b border-gray-100">
                  <td className="py-1 text-gray-700 capitalize">{k.replace(/([A-Z])/g, ' $1')}</td>
                  <td className="py-1 text-right font-medium">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type <code className="bg-gray-100 px-1 rounded">DELETE TEST DATA</code> to confirm</label>
            <input value={phrase} onChange={(e) => setPhrase(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="DELETE TEST DATA" />
          </div>
          <div className="flex gap-3">
            <button onClick={handleDelete} disabled={phrase !== 'DELETE TEST DATA'} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700">
              Delete All Test Data
            </button>
            <button onClick={() => { setState('idle'); setPhrase(''); }} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {state === 'deleting' && <p className="text-gray-600 animate-pulse">Deleting test data…</p>}

      {state === 'done' && (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            <p className="text-sm text-green-800 font-medium">✅ Cleanup complete</p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {Object.entries(counts).map(([k, v]) => (
                <tr key={k} className="border-b border-gray-100">
                  <td className="py-1 text-gray-700 capitalize">{k.replace(/([A-Z])/g, ' $1')}</td>
                  <td className="py-1 text-right font-medium">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {errors.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800 font-medium">⚠️ {errors.length} error(s):</p>
              <ul className="text-xs text-yellow-700 mt-1 list-disc pl-4">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <button onClick={() => { setState('idle'); setPhrase(''); setCounts({}); setErrors([]); }} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
            Done
          </button>
        </div>
      )}

      {state === 'error' && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">{errorMsg}</p>
          </div>
          <button onClick={() => setState('idle')} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Retry</button>
        </div>
      )}
    </div>
  );
};

export default TestDataCleanup;
